import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Plural } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import Popover from '../ui/Popover';
import type { ProfilePublicFolder } from '../../types/api';

interface PublicFoldersMenuProps {
  userId: string;
  folders: ProfilePublicFolder[];
  /** Currently selected folder id, or undefined when viewing the liked list. */
  selectedFolderId?: string;
}

/**
 * Dropdown shown on a public profile when the user has public folders. The
 * trigger reads "N Public Folders"; selecting a folder views that folder's
 * public bookmarks at /p/<id>/folder/<folderId>/<slug>.
 */
function PublicFoldersMenu({
  userId,
  folders,
  selectedFolderId,
}: PublicFoldersMenuProps) {
  const [isOpen, setIsOpen] = useState(false);

  if (folders.length === 0) {
    return null;
  }

  const close = () => setIsOpen(false);

  return (
    <Popover
      open={isOpen}
      onOpenChange={setIsOpen}
      placement="bottom-start"
      role="menu"
      content={
        <div className="min-w-52">
          {folders.map((folder) => (
            <Link
              key={folder.id}
              to="/p/$id/folder/$folderId/$slug"
              params={{ id: userId, folderId: folder.id, slug: folder.slug }}
              onClick={close}
              role="menuitemradio"
              aria-checked={selectedFolderId === folder.id}
              className="menu-item"
            >
              {folder.title}
            </Link>
          ))}
        </div>
      }
    >
      <button
        type="button"
        className="text-copy-secondary hover:text-copy-primary flex items-center gap-1 text-sm"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        title={t`Public folders`}
      >
        <Plural value={folders.length} one="# Public Folder" other="# Public Folders" />
        <svg
          className="h-2 w-2.5 fill-none stroke-current stroke-2"
          viewBox="0 0 10 6"
          aria-hidden
        >
          <path d="M1 1L5 5L9 1" />
        </svg>
      </button>
    </Popover>
  );
}

export default PublicFoldersMenu;
